import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Info, Brain, Heart, Shield, Activity, Gauge, ArrowRight, Users,
  Target, TrendingUp, Wrench, Compass, AlertCircle, Sparkles,
  CheckCircle2, Search, Zap, Award,
} from 'lucide-react';
import RelatedReferences from '../../components/reference/RelatedReferences';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { getLightSectionTheme } from '../../lib/sectionThemes';

// ─────────────────────────────────────────────────────────────
// "The Big Picture" — native re-render of the concentric value-
// engine diagram. Three columns: learning systems on the left feed
// the Value Engine in the middle, business systems on the right
// also feed it, and the engine outputs measurable value back into
// both layers. Subtle indigo→cyan tints match the rest of the app.
// ─────────────────────────────────────────────────────────────
function LearningInputCard({ icon: Icon, label, sub }) {
  return (
    <div className="bg-white border border-indigo-100 rounded-lg px-3 py-2.5 flex items-center gap-2.5 shadow-sm">
      <div className="w-8 h-8 rounded-md bg-indigo-50 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-indigo-600" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-900 leading-tight">{label}</p>
        <p className="text-[10px] text-slate-500 leading-tight">{sub}</p>
      </div>
    </div>
  );
}

function BusinessInputCard({ icon: Icon, label, sub }) {
  return (
    <div className="bg-white border border-cyan-100 rounded-lg px-3 py-2.5 flex items-center gap-2.5 shadow-sm">
      <div className="w-8 h-8 rounded-md bg-cyan-50 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-cyan-700" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-900 leading-tight">{label}</p>
        <p className="text-[10px] text-slate-500 leading-tight">{sub}</p>
      </div>
    </div>
  );
}

function BigPictureDiagram() {
  return (
    <div data-testid="big-picture-diagram">
      {/* Header strip — replaces the dark "The BIG PICTURE" header
          band from the source image, in the light Option A vocab. */}
      <div className="bg-gradient-to-r from-indigo-50 via-white to-cyan-50 border border-slate-200 rounded-t-xl px-6 py-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#44b6d2]">The Big Picture</p>
        <p className="mt-1 text-base md:text-lg text-slate-700 leading-snug max-w-3xl">
          Learning impact is created by <strong className="text-slate-900">aligning learning systems with business systems</strong> to drive measurable value.
        </p>
      </div>

      {/* Three-column composition: Learning (left) → Value Engine
          (center) → Business (right). The "Performance Layer" labels
          from the original sit as the column eyebrows. */}
      <div className="bg-white border-x border-b border-slate-200 rounded-b-xl px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-center">

          {/* LEFT — Learning */}
          <div className="lg:col-span-3 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 px-1">Learning Performance Layer</div>
            <LearningInputCard icon={Users}    label="Audience"    sub="Cohorts" />
            <LearningInputCard icon={Sparkles} label="Experience"  sub="Programs, Engagement" />
            <LearningInputCard icon={Brain}    label="Capability"  sub="Skills, Certifications" />
            <LearningInputCard icon={Wrench}   label="Delivery"    sub="Operations, Systems" />
            <div className="text-[10px] uppercase tracking-wider text-slate-400 px-1 pt-1">Learning Systems →</div>
          </div>

          {/* CENTER — Value Engine */}
          <div className="lg:col-span-6 relative">
            <div className="text-[10px] font-bold uppercase tracking-widest text-center text-[#44b6d2] mb-3">Value Engine</div>
            <div className="relative mx-auto max-w-[420px] aspect-square">
              {/* Outer ring — soft brand wash */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#44b6d2]/8 via-indigo-50/40 to-emerald-50/40" />
              <div className="absolute inset-3 rounded-full border border-[#44b6d2]/20" />
              <div className="absolute inset-10 rounded-full border border-slate-200" />

              {/* 4 quadrant pills — LEARNING / ROI / CAPABILITY / KPI.
                  Each pill is centered (both axes) on the inner of
                  the two outer rings (inset-10 ≈ 10% in container
                  coordinates) at the 12, 3, 6 and 9 o'clock points,
                  so they straddle the ring symmetrically. */}
              {[
                { label: 'LEARNING',   pos: 'top-[10%] left-1/2 -translate-x-1/2 -translate-y-1/2', color: 'border-indigo-200 text-indigo-700' },
                { label: 'ROI',        pos: 'top-1/2 left-[90%] -translate-x-1/2 -translate-y-1/2', color: 'border-emerald-200 text-emerald-700' },
                { label: 'KPI',        pos: 'top-[90%] left-1/2 -translate-x-1/2 -translate-y-1/2', color: 'border-amber-200 text-amber-700' },
                { label: 'CAPABILITY', pos: 'top-1/2 left-[10%] -translate-x-1/2 -translate-y-1/2', color: 'border-cyan-200 text-cyan-700' },
              ].map(q => (
                <div
                  key={q.label}
                  className={`absolute ${q.pos} bg-white border ${q.color} rounded-lg px-3 py-1.5 text-[11px] font-bold tracking-wide shadow-sm whitespace-nowrap`}
                >
                  {q.label}
                </div>
              ))}

              {/* Center medallion — VALUE CREATION. Slightly smaller
                  (w-20) than the original so longer side-pills have
                  room to breathe in narrower viewports. */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-[#44b6d2] text-white flex flex-col items-center justify-center shadow-md ring-4 ring-white">
                <span className="text-[9px] font-semibold tracking-widest opacity-90">VALUE</span>
                <span className="text-[11px] font-bold tracking-wider mt-0.5">CREATION</span>
              </div>

              {/* Cycle arrows — abstract clockwise loop */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
                <defs>
                  <marker id="bp-arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto">
                    <path d="M 0 0 L 8 5 L 0 10 z" fill="#94a3b8" />
                  </marker>
                </defs>
                <path d="M 50 32 A 18 18 0 0 1 68 50" stroke="#cbd5e1" strokeWidth="0.8" fill="none" markerEnd="url(#bp-arrow)" />
                <path d="M 68 50 A 18 18 0 0 1 50 68" stroke="#cbd5e1" strokeWidth="0.8" fill="none" markerEnd="url(#bp-arrow)" />
                <path d="M 50 68 A 18 18 0 0 1 32 50" stroke="#cbd5e1" strokeWidth="0.8" fill="none" markerEnd="url(#bp-arrow)" />
                <path d="M 32 50 A 18 18 0 0 1 50 32" stroke="#cbd5e1" strokeWidth="0.8" fill="none" markerEnd="url(#bp-arrow)" />
              </svg>
            </div>
          </div>

          {/* RIGHT — Business */}
          <div className="lg:col-span-3 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-700 px-1 lg:text-right">Business Performance Layer</div>
            <BusinessInputCard icon={Compass}     label="Strategy"    sub="Goals" />
            <BusinessInputCard icon={TrendingUp}  label="Performance" sub="KPI" />
            <BusinessInputCard icon={Gauge}       label="Capacity"    sub="Resources, Capability" />
            <BusinessInputCard icon={Zap}         label="Execution"   sub="Agility, Efficiency" />
            <div className="text-[10px] uppercase tracking-wider text-slate-400 px-1 pt-1 lg:text-right">← Business Systems</div>
          </div>
        </div>

        {/* Value chain footer — the canonical Learning → C×M×R → KPI
            → ROI ribbon, tying back to the rest of the app. */}
        <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-center gap-2 flex-wrap text-xs">
          {[
            { label: 'Learning',              tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
            { label: 'People Engine (C×M×R)', tone: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
            { label: 'KPI',                   tone: 'bg-amber-50 text-amber-700 border-amber-200' },
            { label: 'ROI',                   tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.label}>
              <span className={`px-2.5 py-1 rounded-md border font-medium ${s.tone}`}>{s.label}</span>
              {i < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// "The People Engine" — three-column panel mirroring the original
// infographic: Problem · Capacity Model · Value. Drops the stock
// photo in favor of an iconic problem statement card and reframes
// the formula as styled equation chips that read on the light
// surface.
// ─────────────────────────────────────────────────────────────
function PeopleEnginePanel() {
  return (
    <div data-testid="people-engine-panel">
      <div className="bg-gradient-to-r from-indigo-50 via-white to-cyan-50 border border-slate-200 rounded-t-xl px-6 py-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#44b6d2]">The People Engine</p>
        <p className="mt-1 text-base md:text-lg text-slate-700 leading-snug max-w-3xl">
          <strong className="text-slate-900">Predict workforce performance before it happens.</strong>
        </p>
      </div>

      <div className="bg-white border-x border-b border-slate-200 rounded-b-xl p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">

          {/* THE PROBLEM */}
          <div className="md:col-span-3 rounded-lg bg-rose-50/40 border border-rose-100 p-5 flex flex-col" data-testid="pe-panel-problem">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertCircle className="h-3.5 w-3.5 text-rose-700" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-rose-700">The Problem</span>
            </div>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              Most organizations cannot quantify whether their workforce can deliver their business goals.
            </p>
            <p className="mt-3 text-sm font-semibold text-rose-700">Until it's too late.</p>
          </div>

          {/* CAPACITY MODEL */}
          <div className="md:col-span-6 rounded-lg bg-slate-50 border border-slate-200 p-5" data-testid="pe-panel-model">
            <div className="text-center text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-4">People Engine Capacity Model</div>
            <div className="flex items-center justify-center gap-1.5 md:gap-2 flex-wrap">
              {[
                { label: 'CAPABILITY',  code: '(C)',  tone: 'border-cyan-200 text-cyan-700' },
                { op: '×' },
                { label: 'MOTIVATION',  code: '(M)',  tone: 'border-amber-200 text-amber-700' },
                { op: '×' },
                { label: 'RETENTION',   code: '(R)',  tone: 'border-emerald-200 text-emerald-700' },
                { op: '+' },
                { label: 'LEARNING\nEFFECTIVENESS', code: '(LE)', tone: 'border-indigo-200 text-indigo-700 bg-indigo-50' },
              ].map((item, i) => (
                item.op ? (
                  <span key={`op-${i}`} className="text-xl font-bold text-slate-400 px-0.5">{item.op}</span>
                ) : (
                  <div
                    key={item.label}
                    className={`bg-white border rounded-lg px-2.5 md:px-3 py-2 text-center min-w-[80px] md:min-w-[92px] shadow-sm ${item.tone}`}
                  >
                    <p className="text-[10px] md:text-[11px] font-bold leading-tight whitespace-pre-line">{item.label}</p>
                    <p className="text-[10px] font-mono opacity-70 mt-0.5">{item.code}</p>
                  </div>
                )
              ))}
            </div>

            {/* Equals result */}
            <div className="mt-4 flex items-center justify-center gap-3">
              <span className="text-xl font-bold text-slate-400">=</span>
              <div className="bg-[#44b6d2] text-white rounded-lg px-4 py-2.5 text-center shadow-md">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-90">People Engine Capacity</p>
                <p className="text-lg font-extrabold tracking-wider mt-0.5">(PE)</p>
              </div>
            </div>

            <p className="mt-4 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600">
              Quantifies your workforce capacity to deliver business performance
            </p>

            {/* Mini value chain */}
            <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
              <span>Learning</span>
              <ArrowRight className="h-3 w-3" />
              <span className="font-semibold text-slate-700">People Engine Capacity</span>
              <ArrowRight className="h-3 w-3" />
              <span>KPI</span>
              <ArrowRight className="h-3 w-3" />
              <span>ROI</span>
            </div>
          </div>

          {/* THE VALUE */}
          <div className="md:col-span-3 rounded-lg bg-emerald-50/40 border border-emerald-100 p-5 flex flex-col" data-testid="pe-panel-value">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                <Award className="h-3.5 w-3.5 text-emerald-700" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">The Value</span>
            </div>
            <ul className="mt-3 space-y-2.5">
              {[
                { icon: Search,        label: 'Predict',  hint: 'workforce capacity before risk shows up in results' },
                { icon: Activity,      label: 'Diagnose', hint: 'which lever (C, M, R or LE) is the bottleneck' },
                { icon: TrendingUp,    label: 'Optimize', hint: 'investment toward the weakest link first' },
                { icon: CheckCircle2,  label: 'Prove',    hint: 'business impact in the language CFOs trust' },
              ].map(v => (
                <li key={v.label} className="flex items-start gap-2.5">
                  <v.icon className="h-3.5 w-3.5 text-emerald-700 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-sm font-semibold text-slate-900">{v.label}</span>
                    <p className="text-[11px] text-slate-600 leading-snug">{v.hint}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PEReferencePage() {
  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="People Engine Reference"
        description="Understanding the People Engine framework — how learning becomes capability, capability becomes KPI, and KPI becomes ROI."
        theme={__pageTheme}
        icon={Users}
        testId="pe-reference-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="pe-reference-page">

      {/* The Big Picture — native diagram (replaces the legacy raster
          infographic). Conveys the same alignment-between-learning-
          and-business story using the live design system. */}
      <BigPictureDiagram />
      <div className="px-1">
        <p className="text-sm text-slate-600 leading-relaxed">
          Learning and business systems feed into a central engine that converts capability into financial outcomes. The <strong className="text-slate-900">Value Engine</strong> connects learning inputs (audience, experience, capability, delivery) with business outcomes (strategy, performance, capacity, execution) through a continuous cycle of <strong>Learning → Capability → KPI → ROI</strong>. The People Engine sits at the heart of this cycle — quantifying how effectively your workforce converts learning into business performance.
        </p>
      </div>

      {/* The People Engine — native three-column panel (Problem ·
          Capacity Model · Value), replacing the raster infographic. */}
      <PeopleEnginePanel />
      <div className="px-1">
        <p className="text-sm text-slate-600 leading-relaxed">
          Most organizations cannot quantify whether their workforce can deliver their business goals — until it's too late. The <strong className="text-slate-900">People Engine Capacity Model</strong> solves this by measuring four dimensions: <strong>Capability</strong> (skills and certifications), <strong>Motivation</strong> (engagement and willingness), <strong>Retention</strong> (stability and experience), and <strong>Learning Effectiveness</strong> (how well training translates to competence). Together, these predict your workforce's capacity to deliver business performance — enabling you to <strong>Predict, Diagnose, Optimize, and Prove</strong> learning impact.
        </p>
      </div>

      {/* Formula */}
      <Card className="border-2 border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-indigo-600" />
            The People Engine Formula
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-indigo-200 rounded-lg p-4 text-center">
            <p className="text-xl font-mono font-bold text-indigo-900">
              PE = (((C x M) x R) + LE) / 2
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Or equivalently: <span className="font-mono">PE = ((PPE x R) + LE) / 2</span> where PPE = C x M
            </p>
          </div>
          <p className="text-sm text-slate-700">
            The People Engine (PE) quantifies how ready your workforce is to deliver business outcomes. It combines four measurable elements into a single score that leaders can track, diagnose, and improve.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Rule of thumb:</strong> PE of 80% means you'll deliver ~80% of plan or need ~20% more time. Like a car engine — the distance and speed you can cover depend on the power and condition of your People Engine.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Brain className="h-4 w-4 text-indigo-500" />
              Capability (C)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p>The percentage alignment of technical and durable skills at assessed proficiency levels versus role/function/task requirements.</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">
              C = avg(min(Achieved / Required, 1)) across all required skills
            </div>
            <p className="text-slate-500">Includes both <strong>technical skills</strong> (Python, Salesforce, SQL) and <strong>durable skills</strong> (communication, leadership, problem solving). A weakness here means your workforce can't do the job.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Heart className="h-4 w-4 text-amber-500" />
              Motivation (M)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p>The percentage willingness to employ capability, shaped by intrinsic and extrinsic drivers.</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">
              M = (Intrinsic + Extrinsic) / 2, normalized to 0–1.0
            </div>
            <p className="text-slate-500"><strong>Intrinsic:</strong> purpose, pride, growth, passion. <strong>Extrinsic:</strong> leadership, recognition, compensation, work-life balance. High motivation unlocks effective use of capability.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-emerald-500" />
              Retention (R)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p>The degree to which critical talent remains in role long enough to reach and sustain full productivity.</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">
              R = (Employees retained / Total employees) x 100
            </div>
            <p className="text-slate-500">The inverse of organizational churn. Low retention is a vicious cycle — replacing an employee costs 0.5-2x annual salary, before missed milestones or customer slippage.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4 text-violet-500" />
              Learning Effectiveness (LE)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p>The additive performance boost from learning programs, captured via the Learning Effectiveness Index (LEI).</p>
            <div className="bg-slate-50 rounded p-2 font-mono text-xs">
              LE = Weighted sum of 5 Pillar scores, normalized to 0–1.0
            </div>
            <div className="text-xs space-y-1 text-slate-500">
              <p>P1: Learning Access & Engagement (20%)</p>
              <p>P2: Learning Performance & Outcomes (30%)</p>
              <p>P3: Workforce Capability & Readiness (20%)</p>
              <p>P4: Operational Efficiency (20%)</p>
              <p>P5: Business Impact & Value Realization (10%)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PPE */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">People Performance Effectiveness (PPE)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <div className="bg-slate-50 rounded p-2 font-mono text-xs">PPE = C x M</div>
          <p>This combined measure identifies the relationship between Capability and Motivation. If either is low, PPE is low — just like a chain limited by its weakest link. This is why C and M are multiplicative: they represent the foundational human capacity before retention and learning are factored in.</p>
        </CardContent>
      </Card>

      {/* Why it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Why the Structure Works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-indigo-600">1</span>
            </div>
            <p><strong>C x M models real-world bottlenecks:</strong> If either skills or willingness is low, output collapses.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-emerald-600">2</span>
            </div>
            <p><strong>Retention (R) keeps power on the road long enough to matter.</strong> High capability and motivation are worthless if people leave before delivering results.</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-violet-600">3</span>
            </div>
            <p><strong>Learning (LE) is additive</strong> because effective learning amplifies performance non-linearly. It boosts total capacity without reducing it if low.</p>
          </div>
        </CardContent>
      </Card>

      {/* The Value Chain */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">The People Engine Value Chain</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center gap-2 flex-wrap py-4">
            {['Learning', 'People Engine Capacity', 'KPI', 'ROI'].map((step, i, arr) => (
              <React.Fragment key={step}>
                <div className="px-4 py-2 rounded-lg bg-indigo-100 text-indigo-700 font-medium text-sm">
                  {step}
                </div>
                {i < arr.length - 1 && <ArrowRight className="h-5 w-5 text-slate-300" />}
              </React.Fragment>
            ))}
          </div>
          <p className="text-sm text-slate-600 text-center mt-2">
            Learning impact is created by aligning learning systems with business systems to drive measurable value.
          </p>
        </CardContent>
      </Card>

      {/* Cross-References */}
      <RelatedReferences activeRef="pe" />
    </div>
    </PageShell>
  );
}
